import {
  cn,
  EditorButtonCode,
  EditorButtonGroupHistory,
  EditorButtonGroupTextMarks,
  EditorButtonLink,
  EditorContent,
  EditorDropdownMenuChatToolName,
  EditorDropdownMenuTable,
  EditorDropdownMenuTextBlockFormat,
  EditorExtensionChatToolName,
  EditorProvider,
  EditorToolbar,
  EditorToolbarSeparator,
  FieldBase,
} from "@qw/ui";
import type { EditorEvents } from "@tiptap/core";
import { useCurrentEditor } from "@tiptap/react";
import {
  type ComponentProps,
  type FC,
  type ForwardedRef,
  forwardRef,
  type MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";
import type { PlaybookFormValues } from "./playbook-editor.types";
import { PlaybookEditorWorkflowFilesDropdownMenu } from "./playbook-editor-workflow-files-dropdown-menu";
import { EditorExtensionPlaybookFile } from "./playbook-workflow-extensions/editor-extension-playbook-file";
import { EditorExtensionTemplateConditional } from "./playbook-workflow-extensions/editor-extension-template-conditional";
import {
  EditorExtensionTemplateSlot,
  type TemplateSlotDeclaredParam,
} from "./playbook-workflow-extensions/editor-extension-template-slot";
import { PlaybookWorkflowTemplateControls } from "./playbook-workflow-template-controls";

type ToolFilterMode = "all" | "whitelist" | "blacklist";

export type PlaybookWorkflowEditorToolbarProps = ComponentProps<typeof EditorToolbar> & {
  getDeclaredParams?: () => TemplateSlotDeclaredParam[];
  isTemplateEditing?: boolean;
};

const PlaybookWorkflowEditorToolbar: FC<PlaybookWorkflowEditorToolbarProps> = ({
  getDeclaredParams,
  isTemplateEditing,
  ...props
}) => {
  const { setValue } = useFormContext<PlaybookFormValues>();
  const files = useWatch<PlaybookFormValues, "files">({ name: "files" });
  const toolFilter = useWatch<PlaybookFormValues, "toolFilter">({ name: "toolFilter" });

  // Derive mode and filtered tools from toolFilter
  const toolFilterMode: ToolFilterMode = useMemo(() => {
    if (!toolFilter) {
      return "all";
    }
    if ("whitelist" in toolFilter && toolFilter.whitelist != null) {
      return "whitelist";
    }
    if ("blacklist" in toolFilter && toolFilter.blacklist != null) {
      return "blacklist";
    }
    return "all";
  }, [toolFilter]);

  const filteredTools = useMemo(() => {
    if (toolFilterMode === "whitelist" && toolFilter && "whitelist" in toolFilter) {
      return toolFilter.whitelist || [];
    }
    if (toolFilterMode === "blacklist" && toolFilter && "blacklist" in toolFilter) {
      return toolFilter.blacklist || [];
    }
    return [];
  }, [toolFilterMode, toolFilter]);

  const handleEnableWhitelist = useCallback(() => {
    setValue("toolFilter", { whitelist: [] }, { shouldDirty: true });
  }, [setValue]);

  const handleAddToWhitelist = useCallback(
    (toolName: string) => {
      const current = (toolFilter && "whitelist" in toolFilter ? toolFilter.whitelist : []) || [];
      if (!current.includes(toolName)) {
        setValue("toolFilter", { whitelist: [...current, toolName] }, { shouldDirty: true });
      }
    },
    [toolFilter, setValue]
  );

  const handleRemoveFromBlacklist = useCallback(
    (toolName: string) => {
      const current = (toolFilter && "blacklist" in toolFilter ? toolFilter.blacklist : []) || [];
      setValue("toolFilter", { blacklist: current.filter((t) => t !== toolName) }, { shouldDirty: true });
    },
    [toolFilter, setValue]
  );

  return (
    <EditorToolbar {...props}>
      <EditorDropdownMenuTextBlockFormat />
      <EditorToolbarSeparator />
      {!isTemplateEditing && (
        <>
          <EditorDropdownMenuChatToolName
            toolFilterMode={toolFilterMode}
            filteredTools={filteredTools}
            onEnableWhitelist={handleEnableWhitelist}
            onAddToWhitelist={handleAddToWhitelist}
            onRemoveFromBlacklist={handleRemoveFromBlacklist}
          />
          <EditorToolbarSeparator />
        </>
      )}
      {!isTemplateEditing && !!files?.length && (
        <>
          <PlaybookEditorWorkflowFilesDropdownMenu files={files} />
          <EditorToolbarSeparator />
        </>
      )}
      {isTemplateEditing && (
        <>
          <PlaybookWorkflowTemplateControls getDeclaredParams={getDeclaredParams} />
          <EditorToolbarSeparator />
        </>
      )}
      <EditorButtonGroupTextMarks />
      <EditorToolbarSeparator />
      <EditorButtonLink />
      <EditorToolbarSeparator />
      <EditorButtonCode />
      <EditorDropdownMenuTable />
      <EditorToolbarSeparator />
      <EditorButtonGroupHistory />
    </EditorToolbar>
  );
};

export interface PlaybookWorkflowEditorProps extends ComponentProps<typeof EditorProvider> {
  getDeclaredParams?: () => TemplateSlotDeclaredParam[];
  isTemplateEditing?: boolean;
  isPaused?: boolean;
  onWorkflowHtmlChange?: (html: string) => void;
}

export interface PlaybookWorkflowEditorHandle {
  insertContentAtCursor: (html: string) => void;
}

/**
 * Removes inline style attributes from pasted HTML content.
 * This ensures consistent styling when users paste content from external sources.
 */
const removeInlineStyles = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("[style]").forEach((el) => el.removeAttribute("style"));
  return doc.body.innerHTML;
};

interface CreatePlaybookWorkflowEditorExtensionsOptions {
  getDeclaredParams?: () => TemplateSlotDeclaredParam[];
  /**
   * Live read of whether the editor is in template mode. A getter, not a boolean:
   * Tiptap builds the editor once, so the slot extension has to read the current
   * mode when the author types rather than capture it at construction.
   */
  isTemplateEditing?: () => boolean;
}

const emptyDeclaredParams = () => [];

export const createPlaybookWorkflowEditorExtensions = ({
  getDeclaredParams,
  isTemplateEditing,
}: CreatePlaybookWorkflowEditorExtensionsOptions = {}) => [
  // Always register BOTH schemas' node types, regardless of mode. Registering
  // only one set would make a type flip lossy: the editor reparses the live HTML
  // under the new schema and the mount effect writes the result straight back
  // into `workflow`, so a Template -> Workflow flip would strip data-tpl-slot /
  // data-tpl-if and a Workflow -> Template flip would downgrade tool/file chips
  // to raw text or file IDs — mutating the draft before the author ever saves.
  //
  // Mode still governs what an author can INSERT (the toolbar hides the
  // workflow-only tool/file controls in template mode) and what the backend
  // accepts (author-time validation rejects a template body that references
  // undeclared params), so the leak this swap originally guarded against is
  // handled by affordances + validation rather than by a lossy schema.
  EditorExtensionChatToolName,
  EditorExtensionPlaybookFile,
  EditorExtensionTemplateSlot.configure({
    getDeclaredParams: getDeclaredParams ?? emptyDeclaredParams,
    // Parsing stays on everywhere (above); only the `{{name}}` input rule and the
    // `{` suggestion menu are gated, so an ordinary Workflow editor — including
    // with templateAuthoring off — behaves exactly as it did before templates.
    isInsertionEnabled: isTemplateEditing ?? (() => false),
  }),
  EditorExtensionTemplateConditional,
];

interface PlaybookWorkflowEditorContentProps {
  /** Set once the author edits the body, so normalization-only syncs aren't persisted. */
  hasUserEditedRef: MutableRefObject<boolean>;
  editable?: boolean;
  getDeclaredParams?: () => TemplateSlotDeclaredParam[];
  isTemplateEditing?: boolean;
  isPaused?: boolean;
  onWorkflowHtmlChange?: (html: string) => void;
  workflowEditorRef: ForwardedRef<PlaybookWorkflowEditorHandle>;
}

const PlaybookWorkflowEditorContent: FC<PlaybookWorkflowEditorContentProps> = ({
  editable,
  hasUserEditedRef,
  getDeclaredParams,
  isTemplateEditing,
  isPaused,
  onWorkflowHtmlChange,
  workflowEditorRef,
}) => {
  const { getValues, setValue } = useFormContext<PlaybookFormValues>();
  const { editor } = useCurrentEditor();

  useImperativeHandle(
    workflowEditorRef,
    () => ({
      insertContentAtCursor: (html: string) => {
        if (!editor) {
          return;
        }

        editor.chain().focus().insertContent(html).run();
        onWorkflowHtmlChange?.(editor.getHTML());
      },
    }),
    [editor, onWorkflowHtmlChange]
  );

  useEffect(() => {
    if (!isPaused && editable && editor) {
      const currentContent = editor.getHTML();

      // Only persist the body when the author actually edited it. Tiptap
      // re-serializes what it parsed (a slot span gains its label text and class,
      // `<p data-tpl-if>` becomes `<div data-tpl-if>`), and AutosaveWrapper watches
      // form VALUES rather than dirty state — and saves immediately on resume — so
      // an unconditional write here would save a rewritten body for a playbook the
      // author only opened, or opened and paused. Real edits already reach the form
      // through the debounced onUpdate handler; this sync exists to flush that
      // debounce, so gating it on "was there an edit" keeps it doing its job
      // without touching untouched content. A new playbook still needs a valid
      // initial value, so sync when there's nothing stored yet.
      const storedWorkflow = getValues("workflow");
      if (hasUserEditedRef.current || !storedWorkflow) {
        setValue("workflow", currentContent, { shouldValidate: true });
      }

      // Local validation state only — never persisted — so it can always track the
      // editor's live view of the body.
      onWorkflowHtmlChange?.(currentContent);
    }
  }, [isPaused, editable, editor, getValues, setValue, onWorkflowHtmlChange, hasUserEditedRef]);

  return (
    <FieldBase name="workflow" className={cn("flex-1", { "text-muted-foreground cursor-not-allowed": !editable })}>
      <div className={cn("flex flex-col flex-1", { "py-0": !editable })}>
        {editable && (
          <div className="sticky -top-5 border-b bg-card z-10">
            <div className="-mx-3">
              <PlaybookWorkflowEditorToolbar
                getDeclaredParams={getDeclaredParams}
                isTemplateEditing={isTemplateEditing}
              />
            </div>
          </div>
        )}
        <EditorContent className="playbook-editor max-w-4xl py-2 flex-1" />
      </div>
    </FieldBase>
  );
};

export const PlaybookWorkflowEditor = forwardRef<PlaybookWorkflowEditorHandle, PlaybookWorkflowEditorProps>(
  ({ editorProps, getDeclaredParams, isTemplateEditing, onWorkflowHtmlChange, ...props }, ref) => {
    const { setValue } = useFormContext<PlaybookFormValues>();

    // The extension SET never varies by mode (both schemas are always registered),
    // so Tiptap's build-once EditorProvider is fine: there is nothing to swap and
    // therefore no remount — which is what previously made a type flip lossy.
    // Mode instead reaches the slot extension through this ref, read at typing
    // time, so toggling the type immediately enables/disables slot authoring
    // without rebuilding the editor.
    const isTemplateEditingRef = useRef(isTemplateEditing);
    isTemplateEditingRef.current = isTemplateEditing;
    const isTemplateEditingGetter = useCallback(() => isTemplateEditingRef.current === true, []);

    const extensions = useMemo(
      () =>
        createPlaybookWorkflowEditorExtensions({
          getDeclaredParams,
          isTemplateEditing: isTemplateEditingGetter,
        }),
      [getDeclaredParams, isTemplateEditingGetter]
    );

    const handleFormUpdate = useDebounceCallback(
      (update: EditorEvents["update"]) => {
        if (!props.editable || props.isPaused) {
          return;
        }
        setValue("workflow", update.editor.getHTML(), { shouldValidate: true });
      },
      250,
      { trailing: true }
    );

    // Flipped by the editor's own update events, i.e. only by real authoring —
    // never by mounting, parsing, or re-serializing existing content.
    const hasUserEditedRef = useRef(false);

    const handleUpdate = useCallback(
      (update: EditorEvents["update"]) => {
        hasUserEditedRef.current = true;
        onWorkflowHtmlChange?.(update.editor.getHTML());
        handleFormUpdate(update);
      },
      [handleFormUpdate, onWorkflowHtmlChange]
    );

    return (
      <EditorProvider
        {...props}
        placeholder="Describe the workflow steps here..."
        extensions={extensions}
        onUpdate={handleUpdate}
        editorProps={{
          ...editorProps,
          transformPastedHTML: removeInlineStyles,
        }}
      >
        <PlaybookWorkflowEditorContent
          editable={props.editable}
          hasUserEditedRef={hasUserEditedRef}
          getDeclaredParams={getDeclaredParams}
          isTemplateEditing={isTemplateEditing}
          isPaused={props.isPaused}
          onWorkflowHtmlChange={onWorkflowHtmlChange}
          workflowEditorRef={ref}
        />
      </EditorProvider>
    );
  }
);

PlaybookWorkflowEditor.displayName = "PlaybookWorkflowEditor";
